/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider, responsiveFontSizes } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Removed Sidenav for full-width UI

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
import SecureRoute from "components/SecureRoute";
import SignOut from "components/SignOut";
import { useAuth } from "context/AuthContext";
import MobileRoot from "mobile/MobileRoot";
import MobileAutoRedirect from "mobile/MobileAutoRedirect";
import { AIAssistantProvider } from "context/AIAssistantContext";
import AIAssistantDrawer from "components/AIAssistantDrawer";
import { setAiContext } from "lib/aiContext";
import { useStorefrontTenant } from "context/StorefrontTenantContext";
import { applyStorefrontDocumentMetadata } from "lib/storefrontMeta";
import StorefrontPreviewRoot from "pages/storefront-preview/StorefrontPreviewRoot";
import { isStorefrontPreviewPath } from "lib/storefrontPreview";
import { isPortalRole } from "lib/portalRoles";

// Material Dashboard 2 React contexts
import { useMaterialUIController } from "context";

// Images
// (brand assets previously used by Sidenav)

function StorefrontNotConfigured({ loading, error }) {
  return (
    <ThemeProvider theme={responsiveFontSizes(theme)}>
      <CssBaseline />
      <MDBox
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={2}
        sx={{ bgcolor: "#f7f8fa" }}
      >
        <MDBox
          sx={{
            width: "100%",
            maxWidth: 520,
            bgcolor: "#fff",
            border: "1px solid rgba(15,15,18,0.1)",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
          }}
        >
          <Icon sx={{ fontSize: 42, color: "#B54708", mb: 1 }}>storefront</Icon>
          <MDBox
            component="h1"
            sx={{
              m: 0,
              fontSize: "1.35rem",
              fontWeight: 800,
              color: "#0F0F12",
            }}
          >
            {loading ? "Loading storefront" : "Storefront not configured"}
          </MDBox>
          <MDBox
            component="p"
            sx={{ mt: 1, mb: 0, color: "#55575C", lineHeight: 1.6 }}
          >
            {loading
              ? "Preparing the catalog for this domain."
              : error ||
                "This domain is not connected to an active storefront."}
          </MDBox>
        </MDBox>
      </MDBox>
    </ThemeProvider>
  );
}

StorefrontNotConfigured.defaultProps = {
  error: "",
};

StorefrontNotConfigured.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
};

function isStorefrontRoute(pathname) {
  const p = String(pathname || "");
  return (
    p === "/" ||
    p === "/qr" ||
    p === "/scan" ||
    p === "/register" ||
    p.startsWith("/register/") ||
    p === "/distributor/apply" ||
    p === "/partners/apply" ||
    p === "/cart" ||
    p === "/checkout" ||
    p === "/account" ||
    p.startsWith("/account/") ||
    p.startsWith("/catalog") ||
    p === "/authentication/sign-in" ||
    p === "/m" ||
    p.startsWith("/m/")
  );
}

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { direction, layout, darkMode } = controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const storefrontTenant = useStorefrontTenant();
  const isPreviewPath = isStorefrontPreviewPath(pathname);

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    const scrollingEl =
      document.scrollingElement || document.documentElement || document.body;
    if (scrollingEl) scrollingEl.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (storefrontTenant.tenant) {
      applyStorefrontDocumentMetadata(storefrontTenant.tenant);
    }
  }, [pathname, storefrontTenant.tenant]);

  // Keep a baseline "where am I" context updated on every navigation.
  // Rich pages (posting rules, build sheets) can still add extra fields via setAiContext.
  useEffect(() => {
    const p = String(pathname || "");
    const screen =
      p === "/ledger/posting-rules" || p.startsWith("/ledger/posting-rules/")
        ? "posting_rules"
        : p === "/build-sheets" || p.startsWith("/build-sheets/")
        ? "build_sheet"
        : p.startsWith("/ledger/")
        ? "ledger"
        : p.startsWith("/payments")
        ? "payments"
        : p.startsWith("/invoices")
        ? "invoices"
        : p.startsWith("/purchase")
        ? "purchase"
        : p.startsWith("/expenses")
        ? "expenses"
        : p.startsWith("/inventory")
        ? "inventory"
        : p.startsWith("/customers")
        ? "customers"
        : p.startsWith("/vendors")
        ? "vendors"
        : p.startsWith("/payroll")
        ? "payroll"
        : p.startsWith("/settings")
        ? "settings"
        : p === "/"
        ? "home"
        : "app";

    setAiContext({
      screen,
      route: { pathname: p },
    });
  }, [pathname]);

  // Get authentication token
  const { token, hasPermission, pluginRole, authLoading } = useAuth();
  const isHomePage = pathname === "/";
  const isMobilePath = pathname === "/m" || pathname.startsWith("/m/");
  const isPublicPage =
    pathname === "/" ||
    pathname === "/qr" ||
    pathname === "/register" ||
    pathname === "/distributor/apply" ||
    pathname === "/partners/apply" ||
    pathname === "/privacy" ||
    pathname === "/delete-account" ||
    pathname.startsWith("/catalog") ||
    pathname === "/authentication/sign-in" ||
    isMobilePath;

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        const element = route.private ? (
          <SecureRoute
            roles={route.roles}
            pluginRoles={route.pluginRoles}
            permissionKey={
              route.permissionKey ||
              (route.type === "collapse" ? route.key : undefined)
            }
          >
            {route.component}
          </SecureRoute>
        ) : (
          route.component
        );
        return (
          <Route exact path={route.route} element={element} key={route.key} />
        );
      }

      return null;
    });

  const configsButton = null;

  // Filter routes: exclude private routes when not logged in, exclude sign-in when logged in
  const filteredRoutes = routes.filter((r) => {
    if (r.private && !token) return false; // Hide private routes when not logged in
    return true;
  });

  // Add Sign Out route when user is authenticated
  const routesWithSignOut = token
    ? [
        ...filteredRoutes,
        {
          type: "collapse",
          name: "Sign Out",
          key: "sign-out",
          icon: <Icon fontSize="small">logout</Icon>,
          route: "/sign-out",
          component: <SignOut />,
        },
      ]
    : filteredRoutes;

  // Responsive typography themes
  const themeResponsive = useMemo(() => responsiveFontSizes(theme), []);
  const themeDarkResponsive = useMemo(() => responsiveFontSizes(themeDark), []);
  const themeRTLResponsive = useMemo(() => responsiveFontSizes(themeRTL), []);
  const themeDarkRTLResponsive = useMemo(
    () => responsiveFontSizes(themeDarkRTL),
    []
  );

  const normalizedPluginRole = String(pluginRole || "").toLowerCase();
  const storefrontInactive =
    !!storefrontTenant.tenant &&
    storefrontTenant.tenant.storefrontEnabled === false;

  // Avoid misrouting immediately after login while AuthContext is hydrating /users/me.
  // (Especially important for customer accounts that should land in the portal, not staff dashboards.)
  if (token && authLoading && !isPreviewPath) return null;
  if (
    isStorefrontRoute(pathname) &&
    !isPreviewPath &&
    (storefrontTenant.loading || !storefrontTenant.tenantKey || storefrontInactive)
  ) {
    return (
      <StorefrontNotConfigured
        loading={storefrontTenant.loading}
        error={
          storefrontInactive
            ? "This storefront is not active for this domain."
            : storefrontTenant.error
        }
      />
    );
  }

  // Portal users (customer/distributor) land in the customer portal. Staff land on
  // /dashboard, which is now a LegacyStaffRedirect that hard-navigates to staff-erp
  // (/staff); the legacy public-web desktop ERP dashboard is decommissioned.
  const defaultAuthedPath = isPortalRole(normalizedPluginRole)
    ? "/account"
    : normalizedPluginRole === "authenticated"
    ? "/auth-dashboard"
    : "/dashboard";

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider
        theme={darkMode ? themeDarkRTLResponsive : themeRTLResponsive}
      >
        <CssBaseline />
        <MobileAutoRedirect />
        {!isPublicPage &&
          !isMobilePath &&
          layout === "dashboard" &&
          configsButton}
        {layout === "vr" && null}
        <AIAssistantProvider>
          <Routes>
            <Route path="/storefront-preview/*" element={<StorefrontPreviewRoot />} />
            <Route path="/m/*" element={<MobileRoot />} />
            {getRoutes(routesWithSignOut)}
            <Route
              path="*"
              element={
                <Navigate to={token ? defaultAuthedPath : "/"} replace />
              }
            />
          </Routes>
          {token && !isMobilePath && !isPreviewPath ? <AIAssistantDrawer /> : null}
        </AIAssistantProvider>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDarkResponsive : themeResponsive}>
      <CssBaseline />
      <MobileAutoRedirect />
      {layout === "dashboard" &&
        !isPublicPage &&
        !isMobilePath &&
        configsButton}
      {layout === "vr" && null}
      <AIAssistantProvider>
        <Routes>
          <Route path="/m/*" element={<MobileRoot />} />
          {getRoutes(routesWithSignOut)}
          <Route
            path="*"
            element={<Navigate to={token ? defaultAuthedPath : "/"} replace />}
          />
        </Routes>
        {token && !isMobilePath ? <AIAssistantDrawer /> : null}
      </AIAssistantProvider>
    </ThemeProvider>
  );
}
